import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CertifiedBaselineForm, COUNTER_LABELS, emptyChecklist } from "@/components/consolidation/CertifiedBaselineForm";

export const BASELINE_STATUS = {
  proposed: ["BOZZA", "bg-amber-50 text-amber-900 border-amber-300"],
  suspended: ["SOSPESA", "bg-orange-50 text-orange-900 border-orange-300"],
  approved: ["APPROVATA", "bg-emerald-50 text-emerald-900 border-emerald-300"],
  revoked: ["REVOCATA", "bg-suite-neutral-100 text-suite-neutral-700 border-suite-neutral-300"],
  rejected: ["RIFIUTATA", "bg-red-50 text-red-800 border-red-200"],
  superseded: ["SOSTITUITA", "bg-suite-neutral-50 text-suite-neutral-500 border-suite-neutral-200"],
};
const fmt = (v) => (v === null || v === undefined ? "—" : Number(v).toFixed(3));

function Version({ b, onAction }) {
  const [label, tone] = BASELINE_STATUS[b.status] || [b.status, ""];
  const verified = b.origin === "payroll_verified";
  const btn = (key, text, extra = {}) => <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => onAction(b, key)} data-testid={`cb-${key}-${b.id}`} {...extra}>{text}</Button>;
  return (
    <div className="border-t border-suite-neutral-100 px-3 py-2 text-xs space-y-1" data-testid={`cb-version-${b.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded border px-1.5 py-0.5 text-[11px] font-bold ${tone}`} data-testid={`cb-status-${b.id}`}>{label}</span>
        <span className="font-medium">{verified ? "Baseline verificata da documentazione paga" : "Baseline ricostruita"} · taglio {String(b.month).padStart(2, "0")}/{b.year}</span>
        {verified && <span className="text-suite-neutral-500">{b.document?.type} {b.document?.period} · rif. {b.document?.reference}</span>}
        <span className="ml-auto flex gap-1">
          {verified && b.status === "proposed" && btn("approve", "Approva", { disabled: !b.approvable })}
          {verified && b.status === "proposed" && btn("suspend", "Sospendi")}
          {verified && b.status === "suspended" && btn("resume", "Riprendi con nuova verifica")}
          {verified && ["proposed", "suspended"].includes(b.status) && btn("reject", "Rifiuta")}
          {b.status === "approved" && btn("revoke", "Revoca")}
        </span>
      </div>
      {verified && <div className="overflow-x-auto"><table className="w-full min-w-[640px] text-[11px]"><tbody>
        {Object.entries(b.counters || {}).map(([c, v]) => (
          <tr key={c} data-testid={`cb-version-counter-${b.id}-${c}`}><td className="pr-2 w-28">{COUNTER_LABELS[c]}</td>
            <td className="pr-2">Gestionale {fmt(v.engine_value)}</td><td className="pr-2">Cedolino {fmt(v.payroll_value)} {v.payroll_unit === "days" ? "gg" : "h"}</td>
            <td className="pr-2">Verificato {fmt(v.verified_value)} {v.unit === "days" ? "gg" : "h"}</td>
            <td className="pr-2 font-semibold">Diff. {fmt(v.difference_value)}</td><td className="text-suite-neutral-500">{v.difference_reason || ""}{v.conversion?.divisor ? ` · divisore ${v.conversion.divisor} (${v.conversion.criterion || v.conversion.source})` : ""}</td></tr>
        ))}
      </tbody></table></div>}
      {(b.anomalies || []).filter((a) => a.severity === "blocking").map((a) => <div key={a.code} className="text-red-700" data-testid={`cb-anomaly-${b.id}-${a.code}`}>• {a.msg}</div>)}
      {b.revocation && <div className="text-suite-neutral-600">Revocata il {b.revocation.revoked_at?.slice(0, 10)}: {b.revocation.reason}</div>}
      {b.approval?.approved && <div className="text-suite-neutral-600">Approvata il {b.approval.approved_at?.slice(0, 10)}: {b.approval.reason}</div>}
    </div>
  );
}

const ACTIONS = {
  approve: ["Approva baseline verificata", "I dati del gestionale al taglio vengono riverificati. Dall'approvazione lo storico fino al taglio è protetto.", (b) => `/counters-consolidation/baselines/${b.id}/verified/approve`],
  suspend: ["Sospendi baseline", "La baseline resta non approvabile fino a una nuova verifica.", (b) => `/counters-consolidation/baselines/${b.id}/verified/suspend`],
  resume: ["Riprendi baseline", "Viene usata la verifica voce per voce impostata nel modulo sopra.", (b) => `/counters-consolidation/baselines/${b.id}/verified/resume`],
  reject: ["Rifiuta baseline", "La versione resta consultabile.", (b) => `/counters-consolidation/baselines/${b.id}/verified/reject`],
  revoke: ["Revoca baseline approvata", "Consentita solo prima dell'attivazione e senza consolidamenti o rettifiche successivi. La protezione dello storico viene rimossa.", (b) => `/counters-consolidation/baselines/${b.id}/revoke`],
};

export function CertifiedBaselinePanel({ companyId, setAction, onChanged }) {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [versions, setVersions] = useState([]);
  const [checklist, setChecklist] = useState(emptyChecklist());
  useEffect(() => {
    setEmployeeId("");
    api.get("/employees", { params: { company_id: companyId } }).then(({ data }) => setEmployees(Array.isArray(data) ? data : data.items || [])).catch((e) => toast.error(apiErr(e)));
  }, [companyId]);
  const load = useCallback(() => {
    if (!employeeId) { setVersions([]); return; }
    api.get(`/counters-consolidation/employees/${employeeId}/baselines`).then(({ data }) => setVersions(data.items)).catch((e) => toast.error(apiErr(e)));
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);
  const onAction = (b, key) => {
    const [title, description, url] = ACTIONS[key];
    setAction({ title, description, confirm: async (reason) => {
      try { await api.post(url(b), key === "resume" ? { reason, checklist } : { reason }); toast.success(`${title}: eseguito`); setAction(null); load(); onChanged(); }
      catch (e) { toast.error(apiErr(e)); }
    } });
  };
  return (
    <section className="space-y-3 border-t border-suite-neutral-200 pt-5" data-testid="certified-baseline-panel">
      <div>
        <h2 className="text-base md:text-lg font-semibold text-suite-neutral-900">Baseline verificata da documentazione paga</h2>
        <p className="text-xs text-suite-neutral-600">Saldi letti sul cedolino a una data di taglio, confrontati con GI Presenze. Approvazione e revoca riservate al Super amministratore.</p>
      </div>
      <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className="h-9 rounded-md border border-suite-neutral-300 px-2 text-sm min-w-64" data-testid="cb-employee-select">
        <option value="">Seleziona dipendente…</option>
        {employees.map((e) => <option key={e.id} value={e.id}>{e.last_name} {e.first_name}</option>)}
      </select>
      {employeeId && <CertifiedBaselineForm employeeId={employeeId} onSaved={() => { load(); onChanged(); }} checklist={checklist} setChecklist={setChecklist} />}
      {employeeId && (
        <div className="rounded-lg border border-suite-neutral-200 bg-white" data-testid="cb-versions">
          <div className="px-3 py-2 text-xs font-semibold bg-suite-neutral-50">Versioni (storico completo)</div>
          {versions.length === 0 ? <div className="px-3 py-2 text-xs text-suite-neutral-500">Nessuna baseline registrata.</div>
            : versions.map((b) => <Version key={b.id} b={b} onAction={onAction} />)}
        </div>
      )}
    </section>
  );
}
